import { hideDialog } from '../base/dialog';
import { MEDIA_TYPE, setVideoMuted } from '../base/media';
import { updateSettings } from '../base/settings';
import { getLocalVideoTrack } from '../base/tracks';
import { NOTIFICATION_TIMEOUT_TYPE, showWarningNotification } from '../notifications';
import { createVirtualBackgroundEffect } from '../stream-effects/virtual-background';

import {
    ADD_CUSTOM_IMAGE,
    BACKGROUND_ENABLED,
    RESET_VIRTUAL_BACKGROUND,
    SET_VIRTUAL_BACKGROUND
} from './actionTypes';
import { VIRTUAL_BACKGROUND_TYPE } from './constants';
import virtualBackgroundLogger from './logger';
import { getVirtualBackgroundOptions } from './reducer';

const getVirtualBackgroundDialog = () =>
    require('./components/web').VirtualBackgroundDialog;

/**
 * Turns the camera off after the virtual background was lost for a reason the
 * user did not ask for.
 *
 * Swapping an effect takes the track off the peerconnection and puts it back
 * (see `MHLocalTrack.setEffect`). When that round trip fails the effect is
 * gone but the camera is still open locally, so the preview keeps moving while
 * remote participants are left looking at a frozen frame - the local user has
 * no way to tell anything went wrong. Muting makes the two agree: the camera
 * is off, it looks off, and turning it back on goes through the normal unmute
 * path. `VIDEO_MUTISM_AUTHORITY.USER` is deliberate - the same authority the
 * toolbar button uses, so the user can simply switch the camera back on.
 *
 * Only call this once `setEffect` has actually been attempted. A failure to
 * *build* the effect - an unsupported browser, say - never touches the track,
 * so there is nothing to recover from and muting would just be rude.
 *
 * @param {Function} dispatch - The Redux dispatch function.
 * @returns {void}
 */
function _cameraOffAfterBackgroundLost(dispatch) {
    dispatch(backgroundEnabled(false));
    dispatch(setVideoMuted(true));
    dispatch(showWarningNotification({
        titleKey: 'virtualBackground.backgroundEffectError',
        descriptionKey: 'virtualBackground.backgroundEffectErrorDes'
    }, NOTIFICATION_TIMEOUT_TYPE.LONG));
}

/**
 * Signals the local participant activate the virtual background video or not.
 *
 * @param {Object} options - Represents the virtual background set options.
 * @param {Object} mhTrack - Represents the meet-hour track that will have background effect applied.
 * @param {boolean} isPreview - Whether this is for preview mode.
 * @returns {Promise}
 */
export function toggleBackgroundEffect(options: Object, mhTrack: Object, isPreview: boolean = false) {
    return async function(dispatch: Function, getState: Function) {
        // For preview mode, update the Redux state immediately
        if (isPreview) {
            dispatch(backgroundEnabled(options.backgroundEffectEnabled));
            dispatch(setVirtualBackground(options));
        }

        const state = getState();
        const virtualBackground = getVirtualBackgroundOptions(state);

        if (mhTrack) {
            if (options.backgroundEffectEnabled) {
                let effect;

                try {
                    effect = await createVirtualBackgroundEffect(virtualBackground, dispatch);
                } catch (error) {
                    // Building the effect failed - an unsupported browser, a
                    // model that would not load. The track has not been touched
                    // at this point, so there is nothing detached to recover
                    // from; report it and leave the camera running.
                    virtualBackgroundLogger.error('Error on create background effect:', error);
                    dispatch(backgroundEnabled(false));
                    dispatch(showWarningNotification({
                        titleKey: 'virtualBackground.backgroundEffectError',
                        descriptionKey: 'virtualBackground.backgroundEffectErrorDes'
                    }, NOTIFICATION_TIMEOUT_TYPE.LONG));

                    return;
                }

                try {
                    await mhTrack.setEffect(effect);
                } catch (error) {
                    virtualBackgroundLogger.error('Error on apply background effect:', error);
                    _cameraOffAfterBackgroundLost(dispatch);

                    return;
                }

                // `setEffect` deliberately swallows its own failures so one bad
                // swap cannot wedge its queue, which means the promise
                // resolving says nothing about whether the effect took. It did
                // if - and only if - the track is now holding this exact
                // instance; the failure path unwinds `_streamEffect` back to
                // undefined. Without this check the camera would be left
                // detached from the peerconnection with the UI still claiming
                // the background is on.
                if (mhTrack._streamEffect !== effect) {
                    virtualBackgroundLogger.warn('Virtual background effect did not take');
                    _cameraOffAfterBackgroundLost(dispatch);
                }
            } else {
                try {
                    await mhTrack.setEffect(undefined);
                    dispatch(backgroundEnabled(false));
                    dispatch(setVirtualBackground(options));
                } catch (error) {
                    virtualBackgroundLogger.error('Error on removing background effect:', error);
                    _cameraOffAfterBackgroundLost(dispatch);
                }
            }
        }
    };
}

/**
 * Signals the local participant virtual background update on new track.
 *
 * @param {Object} Track - Represents the track that will have background effect applied.
 * @returns {Promise}
 */
export const toggleBackgroundDevice = (Track: Object) => async (dispatch: Function, getState: Function) => {
    const options = getVirtualBackgroundOptions(getState());

    if (Track.mhTrack && options.backgroundEffectEnabled && Track.mediaType === MEDIA_TYPE.VIDEO && Track.videoType === 'camera') {
        // A camera created while the background is on already carries it: loadEffects() builds the effect into
        // the new track. Building a second one here and swapping it in cost another effect start plus a
        // remove/re-add of the camera on the peerconnection on every camera change and every room change.
        const applied = Track.mhTrack._streamEffect?._options?.virtualBackground;

        if (applied
                && applied.backgroundType === options.backgroundType
                && applied.virtualSource === options.virtualSource
                && applied.blurValue === options.blurValue) {
            return;
        }

        let effect;

        try {
            effect = await createVirtualBackgroundEffect(options, dispatch);
        } catch (error) {
            // The track has not been touched yet - see the note in
            // `_cameraOffAfterBackgroundLost`.
            virtualBackgroundLogger.error('Error on create background effect:', error);
            dispatch(backgroundEnabled(false));
            dispatch(showWarningNotification({
                titleKey: 'virtualBackground.backgroundEffectError',
                descriptionKey: 'virtualBackground.backgroundEffectErrorDes'
            }, NOTIFICATION_TIMEOUT_TYPE.SHORT));

            return;
        }

        try {
            await Track.mhTrack.setEffect(effect);
        } catch (error) {
            virtualBackgroundLogger.error('Error on apply background effect:', error);
            _cameraOffAfterBackgroundLost(dispatch);

            return;
        }

        // See the note in `toggleBackgroundEffect` - a resolved `setEffect` is
        // not the same as an applied effect.
        if (Track.mhTrack._streamEffect !== effect) {
            virtualBackgroundLogger.warn('Virtual background did not take on track change');
            _cameraOffAfterBackgroundLost(dispatch);
        }
    }
};

/**
 * Sets the selected virtual background image object.
 *
 * @param {Object} options - Represents the virtual background set options.
 * @returns {Object} The action object.
 */
export function setVirtualBackground(options: Object) {
    return {
        type: SET_VIRTUAL_BACKGROUND,
        virtualSource: options?.virtualSource,
        blurValue: options?.blurValue,
        backgroundType: options?.backgroundType,
        selectedThumbnail: options?.selectedThumbnail,
        backgroundEffectEnabled: options?.backgroundEffectEnabled
    };
}

/**
 * Signals the local participant that the background effect has been enabled.
 *
 * @param {boolean} backgroundEffectEnabled - Indicate if virtual background effect is activated.
 * @returns {Object} The action object.
 */
export function backgroundEnabled(backgroundEffectEnabled: boolean) {
    return {
        type: BACKGROUND_ENABLED,
        backgroundEffectEnabled
    };
}

/**
 * Simulates blurred background selection/removal on video background. Used by API only.
 *
 * @param {Object} videoTrack - The targeted video track.
 * @param {string} [blurType] - Blur type to apply. Accepted values are 'slight-blur', 'blur' or 'none'.
 * @param {boolean} muted - Muted state of the video track.
 * @returns {Promise}
 */
export function toggleBlurredBackgroundEffect(videoTrack: Object, blurType: string, muted: boolean) {
    return async function(dispatch: Function) {
        if (muted || !videoTrack || !blurType) {
            return;
        }

        if (blurType === 'none') {
            dispatch(toggleBackgroundEffect({
                backgroundEffectEnabled: false,
                selectedThumbnail: blurType
            }, videoTrack));
        } else {
            dispatch(toggleBackgroundEffect({
                backgroundEffectEnabled: true,
                backgroundType: VIRTUAL_BACKGROUND_TYPE.BLUR,
                blurValue: blurType === 'blur' ? 25 : 8,
                selectedThumbnail: blurType
            }, videoTrack));
        }
    };
}

/**
 * Sets custom background images.
 *
 * @param {Array} customImages - Array of custom image objects.
 * @returns {Object} The action object.
 */
export function setCustomBackground(customImages: Array<Object>) {
    return {
        type: ADD_CUSTOM_IMAGE,
        customImages
    };
}

/**
 * Resets virtual background to default state.
 *
 * @returns {Object} The action object.
 */
export function resetVirtualBackground() {
    return {
        type: RESET_VIRTUAL_BACKGROUND
    };
}

/**
 * Applies the selected virtual background option and updates related settings.
 *
 * @param {Object} newState - The new virtual background state.
 * @param {boolean} isCancel - Whether the action is a cancellation.
 * @returns {Function}
 */
export function submitVirtualBackground(newState: Object, isCancel: boolean = false) {
    return async (dispatch: Function, getState: Function) => {
        const state = getState();
        const track = getLocalVideoTrack(state['features/base/tracks'])?.mhTrack;
        const { localFlipX } = state['features/base/settings'];

        if (newState.options?.selectedThumbnail) {
            await dispatch(toggleBackgroundEffect(newState.options, track, true));
            dispatch(hideDialog(getVirtualBackgroundDialog()));

            if (!isCancel) {
                // Set x scale to default value.
                dispatch(updateSettings({
                    localFlipX
                }));

                virtualBackgroundLogger.info(`Virtual background type: '${
                    typeof newState.options.backgroundType === 'undefined'
                        ? 'none' : newState.options.backgroundType}' applied!`);
            }
        }
    };
}
